import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import PhotoInfoContent from '../components/photos/PhotoInfoContent';
import ReportContentModal from '../components/ReportContentModal';
import {
    Box,
    Typography,
    Card,
    Button,
    Chip,
    IconButton,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Menu,
    MenuItem,
    Fab,
    FormControl,
    InputLabel,
    Select,
    Avatar,
    AvatarGroup,
    Tooltip,
    Checkbox,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider,
    LinearProgress,
    Fade,
} from '@mui/material';
import {
    ArrowBack,
    PhotoLibrary,
    CloudUpload,
    Delete,
    Edit,
    Public,
    Lock,
    AutoAwesome,
    Close,
    MoreVert,
    Download,
    Share,
    Add,
    People,
    Group,
    CheckCircle,
    Error as ErrorIcon,
    ChevronLeft,
    ChevronRight,
    ZoomIn,
    ZoomOut,
    KeyboardArrowUp,
    OutlinedFlag,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

// ============ INTERFACES ============
interface TripPhoto {
    id: string;
    url: string;
    thumbnail: string;
    caption?: string;
    aiTags?: string[];
    aiCaption?: string;
    aiLocationGuess?: string | null;
    tripDestination?: string;
    visibility: 'private' | 'bffs_travel_partners' | 'friends_family' | 'public';
    sharedWith?: string[];
    uploadedAt: string;
    location?: string;
    s3Key?: string;
    fileName?: string;
    fileType?: string;
    uploaderName?: string;
    shareStatus?: string;
}

interface Friend {
    id: string;
    name: string;
    email: string;
    avatar?: string;
}

interface TravelGroup {
    id: string;
    name: string;
    members: string[];
    color: string;
}

interface UploadingFile {
    file: File;
    progress: number;
    status: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
    photoMetadata?: TripPhoto;
}

// ============ MOCK DATA ============
const isVideoUrl = (url: string): boolean => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.avi', '.m4v'];
    const lowerUrl = url.toLowerCase().split('?')[0];
    return videoExtensions.some(ext => lowerUrl.endsWith(ext));
};
// ============ GESTURE CONSTANTS ============
const SWIPE_THRESHOLD = 50;
const SWIPE_VELOCITY_THRESHOLD = 0.3;
const DOUBLE_TAP_DELAY = 300;
const TAP_MOVEMENT_THRESHOLD = 10;

// ============ IMMERSIVE PHOTO VIEWER COMPONENT ============
interface PhotoViewerProps {
    photos: TripPhoto[];
    tripTitle?: string;
    initialIndex: number;
    open: boolean;
    onClose: () => void;
    onEdit: (photo: TripPhoto) => void;
    onShare: (photo: TripPhoto) => void;
    onDelete: (photoId: string) => void;
    friends: Friend[];
    getVisibilityIcon: (visibility: string) => React.ReactNode;
    getVisibilityLabel: (visibility: string) => string;
    getVisibilityColor: (visibility: string) => { bg: string; color: string };
}

const ImmersivePhotoViewer: React.FC<PhotoViewerProps> = ({
    photos,
    tripTitle,
    initialIndex,
    open,
    onClose,
    onEdit,
    onShare,
    onDelete,
    friends,
    getVisibilityIcon,
    getVisibilityLabel,
    getVisibilityColor,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [reportOpen, setReportOpen] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);
    const [zoomScale, setZoomScale] = useState(1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [infoExpanded, setInfoExpanded] = useState(false);

    useEffect(() => { setInfoExpanded(false); }, [currentIndex]);
    useEffect(() => { if (!open) setInfoExpanded(false); }, [open]);

    const containerRef = useRef<HTMLDivElement>(null);
    const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
    const lastTapRef = useRef<number>(0);
    const lastTapPositionRef = useRef<{ x: number; y: number } | null>(null);
    const pinchStartDistanceRef = useRef<number | null>(null);
    const initialPinchScaleRef = useRef<number>(1);
    const hasMoved = useRef(false);
    const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const currentPhoto = photos[currentIndex];

    useEffect(() => {
        if (open) {
            setCurrentIndex(initialIndex);
            setIsZoomed(false);
            setZoomScale(1);
            setPanPosition({ x: 0, y: 0 });
            setShowControls(true);
        }
    }, [open, initialIndex]);

    useEffect(() => {
        if (showControls && !isZoomed) {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 3000);
        }
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, [showControls, isZoomed, currentIndex]);

    const navigatePhoto = useCallback((direction: 'prev' | 'next') => {
        if (isZoomed) return;
        
        const newIndex = direction === 'next' 
            ? Math.min(currentIndex + 1, photos.length - 1)
            : Math.max(currentIndex - 1, 0);
        
        if (newIndex !== currentIndex) {
            setCurrentIndex(newIndex);
            setShowControls(true);
        }
    }, [currentIndex, photos.length, isZoomed]);

    const handleDoubleTap = useCallback((clientX: number, clientY: number) => {
        if (isZoomed) {
            setIsZoomed(false);
            setZoomScale(1);
            setPanPosition({ x: 0, y: 0 });
        } else {
            const container = containerRef.current;
            if (container) {
                const rect = container.getBoundingClientRect();
                const centerX = rect.width / 2;
                const centerY = rect.height / 2;
                const tapX = clientX - rect.left;
                const tapY = clientY - rect.top;
                
                const panX = (centerX - tapX) * 1.5;
                const panY = (centerY - tapY) * 1.5;
                
                setIsZoomed(true);
                setZoomScale(2.5);
                setPanPosition({ x: panX, y: panY });
            } else {
                setIsZoomed(true);
                setZoomScale(2.5);
            }
        }
    }, [isZoomed]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        setShowControls(true);
        
        if (e.touches.length === 2) {
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            pinchStartDistanceRef.current = distance;
            initialPinchScaleRef.current = zoomScale;
            return;
        }

        const touch = e.touches[0];
        touchStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            time: Date.now(),
        };
        hasMoved.current = false;
        setIsDragging(true);
    }, [zoomScale]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (e.touches.length === 2 && pinchStartDistanceRef.current !== null) {
            e.preventDefault();
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            const scale = (distance / pinchStartDistanceRef.current) * initialPinchScaleRef.current;
            const clampedScale = Math.max(1, Math.min(4, scale));
            setZoomScale(clampedScale);
            setIsZoomed(clampedScale > 1);
            return;
        }

        if (!touchStartRef.current || !isDragging) return;

        const touch = e.touches[0];
        const deltaX = touch.clientX - touchStartRef.current.x;
        const deltaY = touch.clientY - touchStartRef.current.y;

        if (Math.abs(deltaX) > TAP_MOVEMENT_THRESHOLD || Math.abs(deltaY) > TAP_MOVEMENT_THRESHOLD) {
            hasMoved.current = true;
        }

        if (isZoomed) {
            setPanPosition(prev => ({
                x: prev.x + deltaX * 0.8,
                y: prev.y + deltaY * 0.8,
            }));
            touchStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: touchStartRef.current.time,
            };
        } else if (hasMoved.current) {
            setDragOffset(deltaX);
        }
    }, [isDragging, isZoomed]);

    const handleTouchEnd = useCallback(() => {
        pinchStartDistanceRef.current = null;

        if (!touchStartRef.current) {
            setIsDragging(false);
            return;
        }

        const deltaX = dragOffset;
        const deltaTime = Date.now() - touchStartRef.current.time;
        const velocity = Math.abs(deltaX) / deltaTime;
        
        const wasTap = !hasMoved.current && deltaTime < 300;

        if (wasTap) {
            const now = Date.now();
            const tapX = touchStartRef.current.x;
            const tapY = touchStartRef.current.y;

            if (lastTapRef.current && lastTapPositionRef.current) {
                const timeSinceLastTap = now - lastTapRef.current;
                const distanceFromLastTap = Math.hypot(
                    tapX - lastTapPositionRef.current.x,
                    tapY - lastTapPositionRef.current.y
                );

                if (timeSinceLastTap < DOUBLE_TAP_DELAY && distanceFromLastTap < 50) {
                    handleDoubleTap(tapX, tapY);
                    lastTapRef.current = 0;
                    lastTapPositionRef.current = null;
                } else {
                    lastTapRef.current = now;
                    lastTapPositionRef.current = { x: tapX, y: tapY };
                    setShowControls(prev => !prev);
                }
            } else {
                lastTapRef.current = now;
                lastTapPositionRef.current = { x: tapX, y: tapY };
            }
        } else if (!isZoomed) {
            const shouldNavigate = Math.abs(deltaX) > SWIPE_THRESHOLD || velocity > SWIPE_VELOCITY_THRESHOLD;

            if (shouldNavigate) {
                if (deltaX < 0) {
                    navigatePhoto('next');
                } else {
                    navigatePhoto('prev');
                }
            }
        }

        setDragOffset(0);
        setIsDragging(false);
        touchStartRef.current = null;
        hasMoved.current = false;
    }, [dragOffset, isZoomed, handleDoubleTap, navigatePhoto]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (target.closest('button, a, [role="button"]')) return;

        setShowControls(true);
        touchStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            time: Date.now(),
        };
        hasMoved.current = false;
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!touchStartRef.current || !isDragging) return;

        const deltaX = e.clientX - touchStartRef.current.x;
        const deltaY = e.clientY - touchStartRef.current.y;

        if (Math.abs(deltaX) > TAP_MOVEMENT_THRESHOLD || Math.abs(deltaY) > TAP_MOVEMENT_THRESHOLD) {
            hasMoved.current = true;
        }

        if (isZoomed) {
            setPanPosition(prev => ({
                x: prev.x + deltaX * 0.5,
                y: prev.y + deltaY * 0.5,
            }));
            touchStartRef.current = {
                x: e.clientX,
                y: e.clientY,
                time: touchStartRef.current.time,
            };
        } else if (hasMoved.current) {
            setDragOffset(deltaX);
        }
    }, [isDragging, isZoomed]);

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        if (!touchStartRef.current) {
            setIsDragging(false);
            return;
        }

        const deltaX = dragOffset;
        const deltaTime = Date.now() - touchStartRef.current.time;
        const velocity = Math.abs(deltaX) / deltaTime;

        const wasTap = !hasMoved.current && deltaTime < 300;

        if (wasTap) {
            const now = Date.now();
            const tapX = e.clientX;
            const tapY = e.clientY;

            if (lastTapRef.current && lastTapPositionRef.current) {
                const timeSinceLastTap = now - lastTapRef.current;
                const distanceFromLastTap = Math.hypot(
                    tapX - lastTapPositionRef.current.x,
                    tapY - lastTapPositionRef.current.y
                );

                if (timeSinceLastTap < DOUBLE_TAP_DELAY && distanceFromLastTap < 50) {
                    handleDoubleTap(tapX, tapY);
                    lastTapRef.current = 0;
                    lastTapPositionRef.current = null;
                } else {
                    lastTapRef.current = now;
                    lastTapPositionRef.current = { x: tapX, y: tapY };
                }
            } else {
                lastTapRef.current = now;
                lastTapPositionRef.current = { x: tapX, y: tapY };
            }
        } else if (!isZoomed) {
            const shouldNavigate = Math.abs(deltaX) > SWIPE_THRESHOLD || velocity > SWIPE_VELOCITY_THRESHOLD;

            if (shouldNavigate) {
                if (deltaX < 0) {
                    navigatePhoto('next');
                } else {
                    navigatePhoto('prev');
                }
            }
        }

        setDragOffset(0);
        setIsDragging(false);
        touchStartRef.current = null;
        hasMoved.current = false;
    }, [dragOffset, isZoomed, handleDoubleTap, navigatePhoto]);

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (isZoomed) {
                    setIsZoomed(false);
                    setZoomScale(1);
                    setPanPosition({ x: 0, y: 0 });
                } else {
                    onClose();
                }
            } else if (e.key === 'ArrowLeft') {
                navigatePhoto('prev');
            } else if (e.key === 'ArrowRight') {
                navigatePhoto('next');
            } else if (e.key === '+' || e.key === '=') {
                if (!isZoomed) {
                    setIsZoomed(true);
                    setZoomScale(2);
                } else if (zoomScale < 4) {
                    setZoomScale(prev => Math.min(4, prev + 0.5));
                }
            } else if (e.key === '-') {
                if (zoomScale > 1) {
                    const newScale = zoomScale - 0.5;
                    if (newScale <= 1) {
                        setIsZoomed(false);
                        setZoomScale(1);
                        setPanPosition({ x: 0, y: 0 });
                    } else {
                        setZoomScale(newScale);
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, isZoomed, zoomScale, onClose, navigatePhoto]);

    const handleZoomIn = () => {
        if (!isZoomed) {
            setIsZoomed(true);
            setZoomScale(2);
        } else if (zoomScale < 4) {
            setZoomScale(prev => Math.min(4, prev + 0.5));
        }
    };

    const handleZoomOut = () => {
        if (zoomScale > 1) {
            const newScale = zoomScale - 0.5;
            if (newScale <= 1) {
                setIsZoomed(false);
                setZoomScale(1);
                setPanPosition({ x: 0, y: 0 });
            } else {
                setZoomScale(newScale);
            }
        }
    };

    if (!open || !currentPhoto) return null;

    const visStyle = getVisibilityColor(currentPhoto.visibility);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{
                sx: {
                    bgcolor: '#000',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                ref={containerRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                sx={{
                    width: '100vw',
                    height: '100vh',
                    position: 'relative',
                    overflow: 'hidden',
                    touchAction: 'none',
                    userSelect: 'none',
                    cursor: isDragging ? 'grabbing' : 'default',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        left: -20,
                        right: -20,
                        bottom: -20,
                        backgroundImage: `url(${currentPhoto.url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'blur(30px) brightness(0.4)',
                        transform: 'scale(1.1)',
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: isZoomed
                            ? `scale(${zoomScale}) translate(${panPosition.x / zoomScale}px, ${panPosition.y / zoomScale}px)`
                            : `translateX(${dragOffset}px)`,
                        transition: isDragging || isZoomed ? 'none' : 'transform 0.3s ease',
                    }}
                >
{isVideoUrl(currentPhoto.url) ? (
                        <Box
                            component="video"
                            src={currentPhoto.url}
                            autoPlay
                            controls
                            loop
                            playsInline
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                            draggable={false}
                        />
                    ) : (
                        <Box
                            component="img"
                            src={currentPhoto.url}
                            alt={currentPhoto.caption || 'Photo'}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                            draggable={false}
                        />
                    )}
                </Box>

                <Fade in={showControls}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            p: 2,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                            }}
                        >
                            <Close />
                        </IconButton>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton size="small" onClick={() => setReportOpen(true)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }} aria-label="Report photo">
                                <OutlinedFlag fontSize="small" />
                            </IconButton>
                            <Chip
                                icon={getVisibilityIcon(currentPhoto.visibility) as React.ReactElement}
                                label={getVisibilityLabel(currentPhoto.visibility)}
                                size="small"
                                sx={{
                                    bgcolor: visStyle.bg,
                                    color: visStyle.color,
                                    fontWeight: 500,
                                    '& .MuiChip-icon': { color: visStyle.color },
                                }}
                            />
                            <Typography variant="body2" sx={{ color: 'white', alignSelf: 'center' }}>
                                {currentIndex + 1} / {photos.length}
                            </Typography>
                        </Box>
                    </Box>
                </Fade>
                <ReportContentModal
                    open={reportOpen}
                    onClose={() => setReportOpen(false)}
                    contentType="photo"
                    contentId={currentPhoto.id}
                    contentLabel="this photo"
                />

                {!isZoomed && photos.length > 1 && (
                    <>
                        <Fade in={showControls && currentIndex > 0}>
                            <IconButton
                                onClick={() => navigatePhoto('prev')}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                    display: { xs: 'none', md: 'flex' },
                                }}
                            >
                                <ChevronLeft sx={{ fontSize: 32 }} />
                            </IconButton>
                        </Fade>
                        <Fade in={showControls && currentIndex < photos.length - 1}>
                            <IconButton
                                onClick={() => navigatePhoto('next')}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                    display: { xs: 'none', md: 'flex' },
                                }}
                            >
                                <ChevronRight sx={{ fontSize: 32 }} />
                            </IconButton>
                        </Fade>
                    </>
                )}

                <Fade in={showControls}>
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 16,
                            bottom: 180,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <IconButton
                            onClick={handleZoomIn}
                            disabled={zoomScale >= 4}
                            sx={{
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            <ZoomIn />
                        </IconButton>
                        <IconButton
                            onClick={handleZoomOut}
                            disabled={zoomScale <= 1}
                            sx={{
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            <ZoomOut />
                        </IconButton>
                    </Box>
                </Fade>

                {isZoomed && (
                    <Fade in>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 80,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bgcolor: 'rgba(0,0,0,0.7)',
                                borderRadius: '20px',
                                px: 2,
                                py: 1,
                                backdropFilter: 'blur(10px)',
                            }}
                        >
                            <Typography variant="caption" sx={{ color: 'white' }}>
                                {Math.round(zoomScale * 100)}% • Double-tap or ESC to exit
                            </Typography>
                        </Box>
                    </Fade>
                )}

                <Fade in={!isZoomed} timeout={0}>
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: { xs: 1.5, sm: 3 },
                            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
                            '@media (orientation: landscape) and (max-height: 500px)': {
                                bottom: 16,
                                left: 16,
                                right: 'auto',
                                width: 320,
                                maxHeight: '60vh',
                                p: 0,
                                background: 'rgba(0,0,0,0.75)',
                                backdropFilter: 'blur(20px)',
                                borderRadius: 2,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        }}
                    >
                        {/* Landscape phone: collapsible header (always visible) */}
                        <Box
                            onClick={(e) => { e.stopPropagation(); setInfoExpanded(prev => !prev); }}
                            onTouchStart={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                            onTouchEnd={(e) => e.stopPropagation()}
                            sx={{
                                display: 'none',
                                '@media (orientation: landscape) and (max-height: 500px)': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1.5,
                                    py: 0.75,
                                    cursor: 'pointer',
                                    borderBottom: infoExpanded ? '1px solid rgba(255,255,255,0.15)' : 'none',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    mr: 1,
                                }}
                            >
                                {tripTitle || 'Details'}
                            </Typography>
                            <KeyboardArrowUp
                                sx={{
                                    color: 'white',
                                    fontSize: 20,
                                    transform: infoExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            />
                        </Box>

                        {/* Body: portrait/desktop always; landscape only when expanded */}
                        <Box
                            onTouchStart={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                            onTouchEnd={(e) => e.stopPropagation()}
                            sx={{
                                '@media (orientation: landscape) and (max-height: 500px)': {
                                    display: infoExpanded ? 'block' : 'none',
                                    p: 1,
                                },
                            }}
                        >
                            <PhotoInfoContent
                                photo={currentPhoto}
                                tripTitle={tripTitle}
                                friends={friends}
                                photos={photos}
                                currentIndex={currentIndex}
                                onShare={onShare}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onSelectPhoto={setCurrentIndex}
                                showPaginationDots={true}
                                suppressTripTitle={infoExpanded}
                            />
                        </Box>
                    </Box>
                </Fade>

                {photos.length > 1 && !isZoomed && (
                    <Fade in={showControls}>
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                bottom: 200,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                color: 'rgba(255,255,255,0.5)',
                                display: { xs: 'block', md: 'none' },
                            }}
                        >
                            Swipe to see more photos
                        </Typography>
                    </Fade>
                )}
            </Box>
        </Dialog>
    );
};

// ============ MAIN COMPONENT ============
const TripPhotosPage: React.FC = () => {
    const navigate = useNavigate();
    const { tripId } = useParams<{ tripId: string }>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [photos, setPhotos] = useState<TripPhoto[]>([]);
    const [tripTitle, setTripTitle] = useState<string>('');
    const [isCompanion, setIsCompanion] = useState(false);
    const [companionRowId, setCompanionRowId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [selectedPhoto, setSelectedPhoto] = useState<TripPhoto | null>(null);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [editCaption, setEditCaption] = useState('');
    const [editVisibility, setEditVisibility] = useState<'private' | 'bffs_travel_partners' | 'friends_family' | 'public'>('private');
    const [editPhotoExpiresAt, setEditPhotoExpiresAt] = useState('');
    const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [friends, setFriends] = useState<Friend[]>([]);
    const [groups] = useState<TravelGroup[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchFriends = async () => {
            try {
                const token = localStorage.getItem('idToken');
                const res = await fetch(`${API_URL}/friends`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success && Array.isArray(data.friends)) {
                    setFriends(data.friends.filter((f: any) => f.status === 'active'));
                }
            } catch (err) {
                console.error('Error fetching friends:', err);
            }
        };
        fetchFriends();
    }, []);

    useEffect(() => {
        fetchPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripId]);

    const fetchPhotos = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('idToken');
            const response = await fetch(`${API_URL}/trips/${tripId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (data.trip?.photos) {
                setPhotos(data.trip.photos);
            }
            if (data.trip?.title || data.trip?.name) {
                setTripTitle(data.trip.title || data.trip.name);
            }
            if (data.trip?.isCompanion) {
                setIsCompanion(true);
                // Find this user's companion row id from sharingPanel
                const storedUser = localStorage.getItem('user');
                if (storedUser && data.trip?.sharingPanel) {
                    try {
                        const user = JSON.parse(storedUser);
                        const myRow = data.trip.sharingPanel.find((r: any) => r.email === user.email || r.displayName === user.displayName);
                        if (myRow) setCompanionRowId(myRow.id);
                    } catch {}
                }
            }
        } catch (err) {
            console.error('Error fetching photos:', err);
            setError('Failed to load photos');
        } finally {
            setLoading(false);
        }
    };

    const publicPhotos = photos.filter(p => p.visibility === 'public');
    const friendsPhotos = photos.filter(p => p.visibility === 'friends_family' || p.visibility === 'bffs_travel_partners');
    const privatePhotos = photos.filter(p => p.visibility === 'private');

    const displayedPhotos = tabValue === 0 ? photos :
        tabValue === 1 ? publicPhotos :
        tabValue === 2 ? friendsPhotos : privatePhotos;

    // ============ FILE UPLOAD HANDLERS ============
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const newFiles: UploadingFile[] = Array.from(files).map(file => ({
            file,
            progress: 0,
            status: 'pending' as const,
        }));

        setUploadingFiles(prev => [...prev, ...newFiles]);
        
        newFiles.forEach((uploadingFile, index) => {
            uploadFile(uploadingFile.file, uploadingFiles.length + index);
        });

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const uploadFile = async (file: File, fileIndex: number) => {
        try {
            setUploadingFiles(prev => prev.map((f, i) => 
                i === fileIndex ? { ...f, status: 'uploading' as const, progress: 10 } : f
            ));

            const token = localStorage.getItem('idToken');
            const presignedResponse = await fetch(`${API_URL}/trips/${tripId}/photos/upload`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    fileName: file.name,
                    fileType: file.type,
                    visibility: 'private',
                    ...(isCompanion ? {
                        uploaded_for_trip_id: tripId,
                        share_status: 'pending',
                        shared_via_companion_id: companionRowId || undefined,
                    } : {}),
                }),
            });

            if (!presignedResponse.ok) {
                throw new Error('Failed to get upload URL');
            }

            const { presignedUrl, photoMetadata } = await presignedResponse.json();

            setUploadingFiles(prev => prev.map((f, i) => 
                i === fileIndex ? { ...f, progress: 30 } : f
            ));

            const uploadResponse = await fetch(presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload to S3');
            }

            setUploadingFiles(prev => prev.map((f, i) => 
                i === fileIndex ? { ...f, progress: 70 } : f
            ));

            const confirmResponse = await fetch(`${API_URL}/trips/${tripId}/photos/confirm`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ photoMetadata }),
            });

            if (!confirmResponse.ok) {
                throw new Error('Failed to save photo metadata');
            }

            setUploadingFiles(prev => prev.map((f, i) => 
                i === fileIndex ? { ...f, status: 'success' as const, progress: 100, photoMetadata } : f
            ));

            setPhotos(prev => [photoMetadata, ...prev]);
            trackEvent('photo_uploaded', { tripId });

        } catch (err) {
            console.error('Upload error:', err);
            setUploadingFiles(prev => prev.map((f, i) => 
                i === fileIndex ? { ...f, status: 'error' as const, error: (err as Error).message } : f
            ));
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            const newFiles: UploadingFile[] = Array.from(files)
                .filter(file => file.type.startsWith('image/'))
                .map(file => ({
                    file,
                    progress: 0,
                    status: 'pending' as const,
                }));

            if (newFiles.length === 0) {
                setError('Please select image files only');
                return;
            }

            setUploadingFiles(prev => [...prev, ...newFiles]);
            
            newFiles.forEach((uploadingFile, index) => {
                uploadFile(uploadingFile.file, uploadingFiles.length + index);
            });
        }
    };

    const clearCompletedUploads = () => {
        setUploadingFiles(prev => prev.filter(f => f.status !== 'success' && f.status !== 'error'));
    };

    const closeUploadDialog = () => {
        if (uploadingFiles.some(f => f.status === 'uploading')) {
            return;
        }
        setUploadDialogOpen(false);
        setUploadingFiles([]);
    };

    // ============ PHOTO VIEWER HANDLERS ============
    const handlePhotoClick = (photo: TripPhoto, index: number) => {
        setSelectedPhoto(photo);
        setSelectedPhotoIndex(index);
        setViewerOpen(true);
    };

    const handleViewerClose = () => {
        setViewerOpen(false);
    };

    const handleViewerEdit = (photo: TripPhoto) => {
        setSelectedPhoto(photo);
        setEditCaption(photo.caption || photo.aiCaption || '');
        setEditVisibility(photo.visibility);
        setSelectedFriends(photo.sharedWith || []);
        setViewerOpen(false);
        setEditDialogOpen(true);
    };

    const handleViewerShare = (photo: TripPhoto) => {
        setSelectedPhoto(photo);
        setSelectedFriends(photo.sharedWith || []);
        setViewerOpen(false);
        setShareDialogOpen(true);
    };

    const handleViewerDelete = (photoId: string) => {
        handleDeletePhoto(photoId);
        setViewerOpen(false);
    };

    // ============ OTHER HANDLERS ============
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, photo: TripPhoto) => {
        event.stopPropagation();
        setSelectedPhoto(photo);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEditClick = () => {
        handleMenuClose();
        if (selectedPhoto) {
            setEditCaption(selectedPhoto.caption || selectedPhoto.aiCaption || '');
            setEditVisibility(selectedPhoto.visibility);
            setSelectedFriends(selectedPhoto.sharedWith || []);
            setEditDialogOpen(true);
        }
    };

    const handleEditSave = async () => {
        if (selectedPhoto) {
            try {
                const updatedPhotos = photos.map(p =>
                    p.id === selectedPhoto.id
                        ? { ...p, caption: editCaption, visibility: editVisibility, sharedWith: selectedFriends }
                        : p
                );
                setPhotos(updatedPhotos);

                const token = localStorage.getItem('idToken');
                await fetch(`${API_URL}/photos/${selectedPhoto.id}/visibility`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ visibility: editVisibility }),
                });

                setEditDialogOpen(false);
            } catch (err) {
                console.error('Error updating photo:', err);
                setError('Failed to update photo');
            }
        }
    };

    const handleChangeVisibility = async (newVisibility: 'private' | 'bffs_travel_partners' | 'friends_family' | 'public') => {
        if (selectedPhoto) {
            if (newVisibility === 'bffs_travel_partners' || newVisibility === 'friends_family') {
                setEditVisibility(newVisibility);
                setSelectedFriends(selectedPhoto.sharedWith || []);
                handleMenuClose();
                setShareDialogOpen(true);
            } else {
                try {
                    const updatedPhotos = photos.map(p =>
                        p.id === selectedPhoto.id ? { ...p, visibility: newVisibility, sharedWith: [] } : p
                    );
                    setPhotos(updatedPhotos);

                    const token = localStorage.getItem('idToken');
                    await fetch(`${API_URL}/photos/${selectedPhoto.id}/visibility`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ visibility: newVisibility }),
                    });

                    handleMenuClose();
                } catch (err) {
                    console.error('Error updating photo visibility:', err);
                    setError('Failed to update photo visibility');
                    fetchPhotos();
                }
            }
        }
    };

    const handleShareSave = async () => {
        if (selectedPhoto) {
            try {
                const updatedPhotos = photos.map(p =>
                    p.id === selectedPhoto.id
                        ? { ...p, visibility: 'bffs_travel_partners' as const, sharedWith: selectedFriends }
                        : p
                );
                setPhotos(updatedPhotos);

                const token = localStorage.getItem('idToken');
                await fetch(`${API_URL}/photos/${selectedPhoto.id}/visibility`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ visibility: 'bffs_travel_partners', friend_ids: selectedFriends }),
                });

                setShareDialogOpen(false);
            } catch (err) {
                console.error('Error updating photo sharing:', err);
                setError('Failed to update photo sharing');
            }
        }
    };

    const handleDeletePhoto = async (photoId: string) => {
        try {
            const updatedPhotos = photos.filter(p => p.id !== photoId);
            setPhotos(updatedPhotos);

            const token = localStorage.getItem('idToken');
            await fetch(`${API_URL}/photos/${photoId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            handleMenuClose();
        } catch (err) {
            console.error('Error deleting photo:', err);
            setError('Failed to delete photo');
        }
    };

    const toggleFriend = (friendId: string) => {
        setSelectedFriends(prev =>
            prev.includes(friendId)
                ? prev.filter(id => id !== friendId)
                : [...prev, friendId]
        );
    };

    const toggleGroup = (groupId: string) => {
        const group = groups.find(g => g.id === groupId);
        if (!group) return;

        setSelectedGroups(prev => {
            if (prev.includes(groupId)) {
                return prev.filter(id => id !== groupId);
            } else {
                return [...prev, groupId];
            }
        });

        setSelectedFriends(prev => {
            const currentSet = new Set(prev);
            if (selectedGroups.includes(groupId)) {
                group.members.forEach(m => currentSet.delete(m));
            } else {
                group.members.forEach(m => currentSet.add(m));
            }
            return Array.from(currentSet);
        });
    };

    const getVisibilityIcon = (visibility: string) => {
        switch (visibility) {
            case 'public': return <Public sx={{ fontSize: 12 }} />;
            case 'bffs_travel_partners': return <People sx={{ fontSize: 12 }} />;
            case 'friends_family': return <People sx={{ fontSize: 12 }} />;
            default: return <Lock sx={{ fontSize: 12 }} />;
        }
    };

    const getVisibilityLabel = (visibility: string) => {
        switch (visibility) {
            case 'public': return 'Public';
            case 'bffs_travel_partners': return 'BFFs';
            case 'friends_family': return 'Friends';
            default: return 'Private';
        }
    };

    const getVisibilityColor = (visibility: string) => {
        switch (visibility) {
            case 'public': return { bg: '#E8F5E9', color: '#4CAF50' };
            case 'bffs_travel_partners': return { bg: '#FFF3E0', color: '#E65100' };
            case 'friends_family': return { bg: '#E8F5F3', color: '#2D6E6A' };
            default: return { bg: '#FFEBEE', color: '#f44336' };
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#4A6FA5' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB' }}>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
               accept="image/*,video/*"
                multiple
                style={{ display: 'none' }}
            />

            <Box
                sx={{
                    background: 'linear-gradient(135deg, #4A6FA5 0%, #3D5A8A 100%)',
                    color: 'white',
                    pt: 2,
                    pb: 3,
                    px: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => navigate(`/trips/${tripId}`)} sx={{ color: 'white', mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Trip Photos
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {photos.length} photos • {publicPhotos.length} public • {friendsPhotos.length} friends • {privatePhotos.length} private
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            borderRadius: '20px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        Upload
                    </Button>
                </Box>

                <Tabs
                    value={tabValue}
                    onChange={(_, newValue) => setTabValue(newValue)}
                    sx={{
                        '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)', textTransform: 'none', minWidth: 'auto', px: 2 },
                        '& .Mui-selected': { color: 'white' },
                        '& .MuiTabs-indicator': { bgcolor: 'white' },
                    }}
                >
                    <Tab label={`All (${photos.length})`} />
                    <Tab label={`Public (${publicPhotos.length})`} icon={<Public sx={{ fontSize: 16 }} />} iconPosition="start" />
                    <Tab label={`Friends (${friendsPhotos.length})`} icon={<People sx={{ fontSize: 16 }} />} iconPosition="start" />
                    <Tab label={`Private (${privatePhotos.length})`} icon={<Lock sx={{ fontSize: 16 }} />} iconPosition="start" />
                </Tabs>
            </Box>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ p: 3 }}>
                {displayedPhotos.length === 0 ? (
                    <Card sx={{ borderRadius: '16px', textAlign: 'center', py: 6 }}>
                        <PhotoLibrary sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            No photos yet
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            Upload photos from your trip to share with friends
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<CloudUpload />}
                            onClick={() => setUploadDialogOpen(true)}
                            sx={{
                                bgcolor: '#4A6FA5',
                                borderRadius: '20px',
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#3D5A8A' }
                            }}
                        >
                            Upload Photos
                        </Button>
                    </Card>
                ) : (
                    <Grid container spacing={2}>
                        {displayedPhotos.map((photo, index) => {
                            const visStyle = getVisibilityColor(photo.visibility);
                            return (
                                <Grid item xs={6} sm={4} md={3} key={photo.id}>
                                    <Card
                                        sx={{
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                                        }}
                                        onClick={() => handlePhotoClick(photo, index)}
                                    >
<Box
                                            sx={{
                                                paddingTop: '100%',
                                                position: 'relative',
                                                bgcolor: '#000',
                                            }}
                                        >
                                            {isVideoUrl(photo.url) ? (
                                                <Box
                                                    component="video"
                                                    src={photo.url}
                                                    muted
                                                    playsInline
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        backgroundImage: `url(${photo.thumbnail || photo.url})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                    }}
                                                />
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, photo)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: 'white',
                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                                }}
                                            >
                                                <MoreVert fontSize="small" />
                                            </IconButton>

                                            {photo.uploaderName && (
                                                    <Chip
                                                        label={`📷 ${photo.uploaderName}`}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            left: 8,
                                                            bgcolor: 'rgba(0,0,0,0.65)',
                                                            color: 'white',
                                                            fontSize: '0.6rem',
                                                            height: 20,
                                                        }}
                                                    />
                                                )}
                                                <Chip
                                                icon={getVisibilityIcon(photo.visibility) as React.ReactElement}
label={getVisibilityLabel(photo.visibility)}
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 8,
                                                    left: 8,
                                                    bgcolor: visStyle.bg,
                                                    color: visStyle.color,
                                                    fontWeight: 500,
                                                    fontSize: '0.7rem',
                                                    height: 24,
                                                    '& .MuiChip-icon': { color: visStyle.color }
                                                }}
                                            />

                                            {photo.visibility === 'friends_family' && photo.sharedWith && photo.sharedWith.length > 0 && (
                                                <AvatarGroup
                                                    max={3}
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 8,
                                                        right: 8,
                                                        '& .MuiAvatar-root': { width: 20, height: 20, fontSize: 8, border: '1px solid white' }
                                                    }}
                                                >
                                                    {photo.sharedWith.map(fId => {
                                                        const friend = friends.find(f => f.id === fId);
                                                        return friend ? (
                                                            <Tooltip key={fId} title={friend.name}>
                                                                <Avatar src={friend.avatar} sx={{ bgcolor: '#2D6E6A' }}>
                                                                    {friend.name[0]}
                                                                </Avatar>
                                                            </Tooltip>
                                                        ) : null;
                                                    })}
                                                </AvatarGroup>
                                            )}
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>

            <ImmersivePhotoViewer
                photos={displayedPhotos}
                tripTitle={tripTitle}
                initialIndex={selectedPhotoIndex}
                open={viewerOpen}
                onClose={handleViewerClose}
                onEdit={handleViewerEdit}
                onShare={handleViewerShare}
                onDelete={handleViewerDelete}
                friends={friends}
                getVisibilityIcon={getVisibilityIcon}
                getVisibilityLabel={getVisibilityLabel}
                getVisibilityColor={getVisibilityColor}
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
            >
                <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    VISIBILITY
                </MenuItem>
                <MenuItem onClick={() => handleChangeVisibility('public')} selected={selectedPhoto?.visibility === 'public'}>
                    <Public sx={{ mr: 1.5, fontSize: 20, color: '#4CAF50' }} />
                    Public
                </MenuItem>
                <MenuItem onClick={() => handleChangeVisibility('bffs_travel_partners')} selected={selectedPhoto?.visibility === 'bffs_travel_partners'}>
                    <People sx={{ mr: 1.5, fontSize: 20, color: '#E65100' }} />
                    BFFs/Travel Partners
                </MenuItem>
                <MenuItem onClick={() => handleChangeVisibility('friends_family')} selected={selectedPhoto?.visibility === 'friends_family'}>
                    <People sx={{ mr: 1.5, fontSize: 20, color: '#2D6E6A' }} />
                    Friends & Family
                </MenuItem>
                <MenuItem onClick={() => handleChangeVisibility('private')} selected={selectedPhoto?.visibility === 'private'}>
                    <Lock sx={{ mr: 1.5, fontSize: 20, color: '#f44336' }} />
                    Private
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleEditClick}>
                    <Edit sx={{ mr: 1.5, fontSize: 20 }} />
                    Edit Caption
                </MenuItem>
                <MenuItem>
                    <Download sx={{ mr: 1.5, fontSize: 20 }} />
                    Download
                </MenuItem>
                <MenuItem onClick={() => handleDeletePhoto(selectedPhoto?.id || '')} sx={{ color: 'error.main' }}>
                    <Delete sx={{ mr: 1.5, fontSize: 20 }} />
                    Delete
                </MenuItem>
            </Menu>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Edit Photo</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Caption"
                        value={editCaption}
                        onChange={(e) => setEditCaption(e.target.value)}
                        multiline
                        rows={2}
                        sx={{ mt: 2, mb: 3 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Visibility</InputLabel>
                        <Select
                            value={editVisibility}
                            label="Visibility"
                            onChange={(e) => setEditVisibility(e.target.value as any)}
                        >
                            <MenuItem value="private">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Lock fontSize="small" sx={{ color: '#f44336' }} />
                                    Private - Only you can see
                                </Box>
                            </MenuItem>
                            <MenuItem value="bffs_travel_partners">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <People fontSize="small" sx={{ color: '#E65100' }} />
                                    BFFs/Travel Partners - Specific friends
                                </Box>
                            </MenuItem>
                            <MenuItem value="friends_family">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <People fontSize="small" sx={{ color: '#2D6E6A' }} />
                                    Friends & Family - Shared with your circle
                                </Box>
                            </MenuItem>
                            <MenuItem value="public">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Public fontSize="small" sx={{ color: '#4CAF50' }} />
                                    Public - Visible to everyone
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    {(editVisibility === 'bffs_travel_partners' || editVisibility === 'friends_family') && (
                        <Alert severity="info" sx={{ borderRadius: '8px' }}>
                            Use the Share button to select specific friends who can see this photo.
                        </Alert>
                    )}
                    {editVisibility !== 'private' && (
                        <TextField
                            fullWidth
                            label="Visibility expires (optional)"
                            type="date"
                            size="small"
                            value={editPhotoExpiresAt}
                            onChange={(e) => setEditPhotoExpiresAt(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                            helperText="Leave blank for no expiry"
                            sx={{ mt: 2 }}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleEditSave}
                        sx={{
                            bgcolor: '#4A6FA5',
                            borderRadius: '20px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#3D5A8A' }
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Share sx={{ color: '#2D6E6A' }} />
                    Let a Friend Peek
                </DialogTitle>
                <DialogContent sx={{ overflowY: 'auto', maxHeight: 420 }}>
                    {groups.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Group fontSize="small" />
                                Travel Groups
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {groups.map(group => (
                                    <Chip
                                        key={group.id}
                                        label={group.name}
                                        onClick={() => toggleGroup(group.id)}
                                        variant={selectedGroups.includes(group.id) ? 'filled' : 'outlined'}
                                        sx={{
                                            borderColor: group.color,
                                            bgcolor: selectedGroups.includes(group.id) ? group.color : 'transparent',
                                            color: selectedGroups.includes(group.id) ? 'white' : group.color,
                                            '&:hover': {
                                                bgcolor: selectedGroups.includes(group.id) ? group.color : `${group.color}20`
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <People fontSize="small" />
                            Friends ({selectedFriends.length} selected)
                        </Typography>
                        <List sx={{ p: 0 }}>
                            {friends.map(friend => (
                                <ListItem
                                    key={friend.id}
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: '8px',
                                        '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                >
                                    <Checkbox
                                        checked={selectedFriends.includes(friend.id)}
                                        onChange={() => toggleFriend(friend.id)}
                                        sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                    />
                                    <ListItemAvatar>
                                        <Avatar src={friend.avatar} sx={{ width: 36, height: 36, bgcolor: '#2D6E6A' }}>
                                            {friend.name[0]}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={friend.name}
                                        secondary={friend.email}
                                        primaryTypographyProps={{ fontWeight: 500 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setShareDialogOpen(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleShareSave}
                        startIcon={<Share />}
                        disabled={selectedFriends.length === 0}
                        sx={{
                            bgcolor: '#2D6E6A',
                            borderRadius: '20px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#009688' }
                        }}
                    >
                        Let {selectedFriends.length} Friend{selectedFriends.length !== 1 ? 's' : ''} Peek
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={uploadDialogOpen} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Upload Photos</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            '&:hover': { borderColor: '#4A6FA5', bgcolor: '#faf4fc' }
                        }}
                        onClick={handleUploadClick}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <CloudUpload sx={{ fontSize: 48, color: '#4A6FA5', mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Click to upload photos
                        </Typography>
                        <Typography color="text.secondary">
                            or drag and drop files here
                        </Typography>
                    </Box>

                    {uploadingFiles.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Uploading {uploadingFiles.length} file{uploadingFiles.length !== 1 ? 's' : ''}
                                </Typography>
                                {uploadingFiles.every(f => f.status === 'success' || f.status === 'error') && (
                                    <Button size="small" onClick={clearCompletedUploads}>
                                        Clear
                                    </Button>
                                )}
                            </Box>
                            {uploadingFiles.map((file, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        {file.status === 'success' && <CheckCircle sx={{ color: '#4CAF50', fontSize: 18 }} />}
                                        {file.status === 'error' && <ErrorIcon sx={{ color: '#f44336', fontSize: 18 }} />}
                                        {(file.status === 'pending' || file.status === 'uploading') && (
                                            <CircularProgress size={16} sx={{ color: '#4A6FA5' }} />
                                        )}
                                        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {file.file.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {file.status === 'success' ? 'Done' : 
                                             file.status === 'error' ? 'Failed' : 
                                             `${file.progress}%`}
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={file.progress}
                                        sx={{ 
                                            borderRadius: 1,
                                            bgcolor: file.status === 'error' ? '#ffebee' : '#f3e5f5',
                                            '& .MuiLinearProgress-bar': {
                                                bgcolor: file.status === 'error' ? '#f44336' : 
                                                         file.status === 'success' ? '#4CAF50' : '#4A6FA5'
                                            }
                                        }}
                                    />
                                    {file.error && (
                                        <Typography variant="caption" color="error">
                                            {file.error}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Alert severity="info" sx={{ mt: 2, borderRadius: '8px' }}>
                        <AutoAwesome sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        AI will automatically tag and caption your photos
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={closeUploadDialog} 
                        sx={{ textTransform: 'none' }}
                        disabled={uploadingFiles.some(f => f.status === 'uploading')}
                    >
                        {uploadingFiles.some(f => f.status === 'uploading') ? 'Uploading...' : 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Fab
                color="primary"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    bgcolor: '#4A6FA5',
                    display: { xs: 'flex', md: 'none' },
                    '&:hover': { bgcolor: '#3D5A8A' },
                }}
                onClick={() => setUploadDialogOpen(true)}
            >
                <Add />
            </Fab>
        </Box>
    );
};

export default TripPhotosPage;
